import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet'
import L from 'leaflet';
import { InfoBox } from './InfoBox';
import { Legend } from './Legend';
import { DataScopeSelector } from './DataScopeSelector';
import { getColor, round, getPerc } from './util';

import './index.css';

import illinois_bg_data from './data/illinois_shp_data_bg_w_linked_ext.min.json';

const dataScopes = [
    {
        name: "2016",
        key: "bg_2016_n_case",
    },
    {
        name: "2017",
        key: "bg_2017_n_case",
    },
    {
        name: "2018",
        key: "bg_2018_n_case",
    },
    {
        name: "2019",
        key: "bg_2019_n_case",
    },
    {
        name: "2020",
        key: "bg_2020_n_case",
    },
    {
        name: "2021",
        key: "bg_2021_n_case",
    },
    {
        name: "2022",
        key: "bg_2022_n_case",
    }
];

const dataWithHospitalInfoOrNot = [
    {
        name: "Yes",
        key: "_linked",
    },
    {
        name: "No",
        key: "",
    }
];

const colors = ["#30cb00", "#a4fba6", "#ffffff", "#ffbaba", "#ff0000", "#800000", "#000000"];
const colorScale = [-0.5, -0.05, 0.05, 0.5, 1, 2];

export default function ChoroplethMapBG() {
    const [selectedArea, setSelectedArea] = useState(null);
    const [hoveredArea, setHoveredArea] = useState(null);

    const [startingYear, setStartingYear] = useState("2016");
    const [endingYear, setEndingYear] = useState("2016");
    const [dataWithHospitalInfoOnly, setDataWithHospitalInfoOnly] = useState("Yes");

    const geoMap = useRef(null);

    const handleStartingYearChange = (event) => {
        setStartingYear(dataScopes.find(element => element.key === event.target.value)["name"]);
    }

    const handleEndingYearChange = (event) => {
        setEndingYear(dataScopes.find(element => element.key === event.target.value)["name"]);
    }

    const handleHospitalInfoScopeChange = (event) => {
        setDataWithHospitalInfoOnly(dataWithHospitalInfoOrNot.find(element => element.key === event.target.value)["name"]);
    }

    const highlightFeature = (e) => {
        let layer = e.target;
        layer.setStyle({
            color: '#444',
            weight: 2
        });
        layer.bringToFront();
        setHoveredArea(layer.feature.properties);
    }

    const resetHighlight = (e) => {
        e.target.setStyle({
            color: '#888',
            weight: 1
        });
        setHoveredArea(null);
    }

    // Builds the tooltip markup for one feature from the current selection.
    const tooltipContentFor = useCallback((properties) => {
        if (startingYear === endingYear) {
            return `<div><span>Please select two different years...</span></div>`;
        }

        let curr_bg = properties["GEOID"];
        let crashChangePerc = 0;
        let startingYearNumber = 0;
        let endingYearNumber = 0;

        let starting_n_drivers = 0;
        let starting_n_passengers = 0;
        let starting_n_pedestrains = 0;
        let starting_n_pedalcyclists = 0;
        let starting_average_iss = 0;
        let starting_drug_involvement_perc = 0.0;

        let ending_n_drivers = 0;
        let ending_n_passengers = 0;
        let ending_n_pedestrains = 0;
        let ending_n_pedalcyclists = 0;
        let ending_average_iss = 0;
        let ending_drug_involvement_perc = 0.0;

        if (dataWithHospitalInfoOnly === "Yes") {
            startingYearNumber = properties["bg_" + startingYear + "_n_case_linked"];
            endingYearNumber = properties["bg_" + endingYear + "_n_case_linked"];
            starting_n_drivers = properties["bg_" + startingYear + "_n_drivers_linked"];
            starting_n_passengers = properties["bg_" + startingYear + "_n_passengers_linked"];
            starting_n_pedestrains = properties["bg_" + startingYear + "_n_pedestrians_linked"];
            starting_n_pedalcyclists = properties["bg_" + startingYear + "_n_pedalcyclists_linked"];
            starting_average_iss = properties["bg_" + startingYear + "_avg_iss_linked"].toFixed(2);
            starting_drug_involvement_perc = getPerc(properties["bg_" + startingYear + "_n_drug_involved_linked"], properties["bg_" + startingYear + "_n_drug_not_involved_linked"]).toFixed(2);
            ending_n_drivers = properties["bg_" + endingYear + "_n_drivers_linked"];
            ending_n_passengers = properties["bg_" + endingYear + "_n_passengers_linked"];
            ending_n_pedestrains = properties["bg_" + endingYear + "_n_pedestrians_linked"];
            ending_n_pedalcyclists = properties["bg_" + endingYear + "_n_pedalcyclists_linked"];
            ending_average_iss = properties["bg_" + endingYear + "_avg_iss_linked"].toFixed(2);
            ending_drug_involvement_perc = getPerc(properties["bg_" + endingYear + "_n_drug_involved_linked"], properties["bg_" + endingYear + "_n_drug_not_involved_linked"]).toFixed(2);
        } else {
            startingYearNumber = properties["bg_" + startingYear + "_n_case"];
            endingYearNumber = properties["bg_" + endingYear + "_n_case"];
            starting_n_drivers = properties["bg_" + startingYear + "_n_drivers"];
            starting_n_passengers = properties["bg_" + startingYear + "_n_passengers"];
            starting_n_pedestrains = properties["bg_" + startingYear + "_n_pedestrians"];
            starting_n_pedalcyclists = properties["bg_" + startingYear + "_n_pedalcyclists"];
            ending_n_drivers = properties["bg_" + endingYear + "_n_drivers"];
            ending_n_passengers = properties["bg_" + endingYear + "_n_passengers"];
            ending_n_pedestrains = properties["bg_" + endingYear + "_n_pedestrians"];
            ending_n_pedalcyclists = properties["bg_" + endingYear + "_n_pedalcyclists"];
        }

        if (startingYearNumber === 0) {
            startingYearNumber += 1;
        }
        if (endingYearNumber - startingYearNumber !== 0) {
            crashChangePerc = round((endingYearNumber - startingYearNumber) / (startingYearNumber) * 100, 2);
        }

        if (dataWithHospitalInfoOnly === "Yes") {
            return `<div><span>
                        Block Group: ${curr_bg}</br>
                        </br>
                        Relative change of crashes: ${crashChangePerc}%</br>
                        </br>
                        Starting Year: ${startingYear}</br>
                        Crashes: ${startingYearNumber}</br>
                        Drivers: ${starting_n_drivers}</br>
                        Passengers: ${starting_n_passengers}</br>
                        Pedestrains: ${starting_n_pedestrains}</br>
                        Pedalcyclists: ${starting_n_pedalcyclists}</br>
                        Avg. ISS: ${starting_average_iss}</br>
                        Drug involved (person): ${starting_drug_involvement_perc}%</br>
                        </br>
                        Ending Year: ${endingYear}</br>
                        Crashes: ${endingYearNumber}</br>
                        Drivers: ${ending_n_drivers}</br>
                        Passengers: ${ending_n_passengers}</br>
                        Pedestrains: ${ending_n_pedestrains}</br>
                        Pedalcyclists: ${ending_n_pedalcyclists}</br>
                        Avg. ISS: ${ending_average_iss}</br>
                        Drug involved (person): ${ending_drug_involvement_perc}%</br>
                        </span></div>`;
        }

        return `<div><span>
                        BLock Group: ${curr_bg}</br>
                        </br>
                        Relative change of crashes: ${crashChangePerc}%</br>
                        </br>
                        Starting Year: ${startingYear}</br>
                        Crashes: ${startingYearNumber}</br>
                        Drivers: ${starting_n_drivers}</br>
                        Passengers: ${starting_n_passengers}</br>
                        Pedestrains: ${starting_n_pedestrains}</br>
                        Pedalcyclists: ${starting_n_pedalcyclists}</br>
                        </br>
                        Ending Year: ${endingYear}</br>
                        Crashes: ${endingYearNumber}</br>
                        Drivers: ${ending_n_drivers}</br>
                        Passengers: ${ending_n_passengers}</br>
                        Pedestrains: ${ending_n_pedestrains}</br>
                        Pedalcyclists: ${ending_n_pedalcyclists}</br>
                        </span></div>`;
    }, [startingYear, endingYear, dataWithHospitalInfoOnly]);

    // react-leaflet only calls onEachFeature when it builds the layer, so tooltip
    // text has to be refreshed here whenever the year/hospital selection changes.
    useEffect(() => {
        if (!geoMap.current) {
            return;
        }
        geoMap.current.getLayers().forEach((layer) => {
            if (layer.feature) {
                layer.setTooltipContent(tooltipContentFor(layer.feature.properties));
            }
        });
    }, [tooltipContentFor]);

    const onEachFeature = useCallback((feature, layer) => {
        if (feature.properties["GEOID"] === '170978630061') return;

        layer.bindTooltip(`<div><span>Please select the starting and ending years...</span></div>`, { sticky: true });

        layer.on({
            mouseover: highlightFeature,
            mouseout: resetHighlight,
            click: () => setSelectedArea(feature.properties)
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const style = useCallback((feature) => {
        let crashChangePerc = 0;
        let startingYearNumber = 0;
        let endingYearNumber = 0;
        if (dataWithHospitalInfoOnly === "Yes") {
            startingYearNumber = feature.properties["bg_" + startingYear + "_n_case_linked"];
            endingYearNumber = feature.properties["bg_" + endingYear + "_n_case_linked"];
        } else {
            startingYearNumber = feature.properties["bg_" + startingYear + "_n_case"];
            endingYearNumber = feature.properties["bg_" + endingYear + "_n_case"];
        }

        if (startingYear !== endingYear) {
            if (startingYearNumber === 0) {
                startingYearNumber += 1;
            }
            if (endingYearNumber - startingYearNumber !== 0) {
                crashChangePerc = (endingYearNumber - startingYearNumber) / startingYearNumber;
            }
        }

        let mapStyle = {
            fillColor: getColor(crashChangePerc, colors, colorScale),
            weight: 1,
            opacity: 1,
            color: '#888',
            dashArray: '3',
            fillOpacity: 0.7
        };

        return mapStyle;
    }, [startingYear, endingYear, dataWithHospitalInfoOnly]);

    const geoJsonComponent = useMemo(
        () => <GeoJSON data={illinois_bg_data} style={style} onEachFeature={onEachFeature} ref={geoMap} />,
        [style, onEachFeature]
    );

    return (
        <div className='mapContainer' >
            <p>Starting Year: <DataScopeSelector options={dataScopes} value={startingYear} changeHandler={handleStartingYearChange} />&nbsp;&nbsp;&nbsp;Ending Year: <DataScopeSelector options={dataScopes} value={endingYear} changeHandler={handleEndingYearChange} /></p>
            <p>Data with Hospital Data Only: <DataScopeSelector options={dataWithHospitalInfoOrNot} value={dataWithHospitalInfoOnly} changeHandler={handleHospitalInfoScopeChange} /></p>
            <MapContainer center={[40, -89]}
                zoomControl={false}
                zoom={8}
                zoomSnap={0.5}
                zoomDelta={0.5}
                wheelPxPerZoomLevel={120}
                maxBoundsViscosity={0.5}
                maxBounds={L.latLngBounds(new L.LatLng(85, -210), new L.LatLng(-85, 210))}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {geoJsonComponent}
                <InfoBox data={selectedArea} dataType="Census block group"/>
                <Legend scale={colorScale} startingYear={startingYear} endingYear={endingYear} dataWithHospitalInfoOnly={dataWithHospitalInfoOnly} colors={colors} hoveredArea={hoveredArea} dataType="bg" />
            </MapContainer>
            <p>Footnotes:</p>
            <p>1. Relative change of crashes = (ending year - starting year) / starting year * 100%</p>
            <p>2. ISS stands for Injury Severity Score</p>
        </div>
    );
}